import * as styles from 'src/components/financial-summary/financial-summary-table.module.scss'
import { FinancialSummary } from 'src/shared/financial-summary.dto'
import { getChangeClass } from 'src/shared/utils'

interface TableData {
  columns: ReadonlyArray<TableColumn>
  rows: ReadonlyArray<TableRow>
  brief: string
}

interface TableColumn {
  label: string
  squashed?: boolean
  hidden?: boolean
  className?: string
}

interface TableRow {
  label: string
  id?: string
  unit?: string
  values?: ReadonlyArray<string | null>
}

export function buildTableData(
  financialSummaryData: FinancialSummary,
): TableData {
  const columns = buildTableColumns(financialSummaryData)
  return {
    columns,
    rows: buildTableRows(financialSummaryData, columns),
    brief: financialSummaryData.meta.financialBrief,
  }
}

function buildTableColumns(data: FinancialSummary): ReadonlyArray<TableColumn> {
  return [
    { label: `Year End ${data.meta.yearEnd}` },
    { label: 'Unit', hidden: true, squashed: true },
    ...data.meta.historicDates.map((date) => ({
      label: `${date}`,
      className: 'stk-text-align-r',
    })),
    ...data.meta.estimateDates.map((date) => ({
      label: `${date}E`,
      className: `stk-text-align-r stk-table-cell-highlight`,
    })),
    { label: 'CAGR / Avg', className: `stk-text-align-r` },
  ]
}

function buildTableRows(
  financialSummaryData: FinancialSummary,
  columns: ReadonlyArray<TableColumn>,
): ReadonlyArray<TableRow> {
  const { data } = financialSummaryData
  const { totalRevenue, epsGrowth } = data
  const values = getColumnsPlaceholderColumns(columns)
  return [
    {
      label: 'Total Revenue',
      id: 'total-sales-local-currency-854',
      unit: data.totalRevenue.unit,
      values: formatValues([
        ...totalRevenue.historicData,
        ...totalRevenue.estimateData,
        totalRevenue.cagr,
      ]),
    },
    {
      label: 'Operating Profit',
      id: 'operating-profit-625',
      unit: 'm',
      values,
    },
    {
      label: 'Net Profit',
      id: 'net-income-4907',
      unit: 'm',
      values,
    },
    {
      label: 'EPS Reported',
      id: 'diluted-eps-excluding-extrordinary-items-4914',
      values,
    },
    {
      label: 'Diluted Normalised EPS',
      id: 'diluted-normalized-eps-4916',
      values,
    },
    {
      label: 'EPS Growth',
      id: 'earnings-per-share-growth-589',
      unit: epsGrowth.unit,
      values: formatValues([
        ...epsGrowth.historicData,
        ...epsGrowth.estimateData,
        epsGrowth.cagr,
      ]),
    },
    {
      label: 'PE Ratio',
      id: 'price-to-earnings-ratio-723',
      unit: 'x',
      values,
    },
    {
      label: 'PEG',
      id: 'price-earnings-to-growth-5083',
      values,
    },
    {
      label: 'Profitability',
    },
    {
      label: 'Operating Margin',
      id: 'operating-profit-margin-638',
      unit: '%',
      values,
    },
    {
      label: 'ROA',
      id: 'return-on-average-assets-795',
      unit: '%',
      values,
    },
    {
      label: 'ROCE',
      id: 'return-on-capital-employed-939',
      unit: '%',
      values,
    },
    {
      label: 'ROE',
      id: 'return-on-equity-817',
      unit: '%',
      values,
    },
    {
      label: 'Cashflow',
    },
    {
      label: 'Op. Cashflow ps',
      id: 'operating-cash-flow-per-share-599',
      values,
    },
    {
      label: 'Capex ps',
      id: 'capital-expenditure-per-share-5105',
      values,
    },
    {
      label: 'Free Cashflow ps',
      id: 'free-cash-flow-per-share-375',
      values,
    },
    {
      label: 'Dividends',
    },
    {
      label: 'Dividend ps',
      id: 'dividends-per-share-common-stock-primary-issue-4915',
      values,
    },
    {
      label: 'Dividend Growth',
      id: 'dividend-per-share-growth-ttm-5084',
      unit: '%',
      values,
    },
    {
      label: 'Dividend Yield',
      id: 'dividend-yield-288',
      unit: '%',
      values,
    },
    {
      label: 'Dividend Cover',
      id: 'dividend-cover-252',
      unit: 'x',
      values,
    },
    {
      label: 'Balance Sheet',
    },
    {
      label: 'Cash etc',
      id: 'cash-short-term-investments-5106',
      unit: 'm',
      values,
    },
    {
      label: 'Working Capital',
      id: 'working-capital-938',
      unit: 'm',
      values,
    },
    {
      label: 'NFA',
      id: 'net-fixed-assets-last-interim-period-532',
      unit: 'm',
      values,
    },
    {
      label: 'Net Debt',
      id: 'net-debt-520',
      unit: 'm',
      values,
    },
    {
      label: 'Book Value',
      id: 'book-value-last-interim-period-129',
      unit: 'm',
      values,
    },
    {
      label: 'Diluted Weighted Average Shares',
      id: 'diluted-weighted-average-shares-4913',
      unit: 'm',
      values,
    },
    {
      label: 'Book Value ps',
      id: 'book-value-per-share-last-interim-period-116',
      values,
    },
  ]
}

function getColumnsPlaceholderColumns(
  columns: ReadonlyArray<TableColumn>,
): ReadonlyArray<null> {
  const [nameColumn, unitColumn, ...dataColumns] = columns
  return dataColumns.map(() => null)
}

export function getColumnClassName(
  columns: ReadonlyArray<TableColumn>,
  dataColumnIndex: number,
): string {
  const [nameColumn, unitColumn, ...dataColumns] = columns
  return dataColumns[dataColumnIndex]?.className || ''
}

export function getValueClassName(value: string): string {
  let className: string = ''

  if (value.startsWith('+') || value.startsWith('-')) {
    className = getChangeClass(parseFloat(value))
  }

  return className
}

function formatValues(
  values: ReadonlyArray<string | null>,
): ReadonlyArray<string> {
  return values.map((value) => (value ? value : 'n/a'))
}
